import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Tag,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import noop from 'lodash/noop';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import {
  fetchInternalKnowledgeData,
  KnowledgeBaseResult,
  knowledgeBasesApi,
  KnowledgeBaseType,
} from 'apis/knoledgeBases';
import {
  HookFormSelectionListSingle,
  HookFormText,
  HookFormTextarea,
} from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './knowledgeBaseModal.module.scss';

const formId = 'knowledge-base-form';

type FieldValues = {
  knowledgeType: KnowledgeBaseType;
  api_key: string;
  token: string;
  external_source_id: string;
};

export const KnowledgeBaseModal = createAppModal(
  { type: 'settings:knowledgeBaseModal' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const arg = modal.arg;

    const flags = useFeatureFlag(['knowledge-access']);
    const navigate = useAppNavigate();
    const notify = useNotifications();

    const knowledgeTypes = [
      { value: 'elevio', label: 'Elevio' },
      { value: 'dixa_knowledge', label: 'Dixa Knowledge' },
    ].filter(
      (type) => !(type.value === 'dixa_knowledge' && !flags.knowledgeAccess),
    );

    const title = arg?.knowledgeBase ? 'Edit' : 'Add';

    const [knowledgeData, setKnowledgeData] = useState<KnowledgeBaseResult[]>(
      [],
    );

    useEffect(() => {
      const fetchAuthData = async () => {
        const knowledgeData = await fetchInternalKnowledgeData();
        setKnowledgeData(knowledgeData);
      };

      fetchAuthData();
    }, []);

    const { control, handleSubmit, formState } = useForm<FieldValues>({
      defaultValues: {
        knowledgeType: arg?.knowledgeBase?.source || 'elevio',
        api_key: '',
        token: '',
        external_source_id: arg?.knowledgeBase?.external_source_id || '',
      },
      mode: 'all',
    });

    const selectedKnowledgeType = useWatch({
      control,
      name: 'knowledgeType',
    });

    const [updateKnowledgeBase, { isLoading: isUpdating }] =
      knowledgeBasesApi.useUpdateKnowledgeBaseMutation();
    const [createKnowledgeBase, { isLoading: isCreating }] =
      knowledgeBasesApi.useCreateKnowledgeBaseMutation();

    const notifySuccess = (text: string) => {
      notify(
        {
          title: 'Success',
          text,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );
    };
    const notifyError = (errorText: string) => {
      notify(
        {
          title: errorText,
          text: 'Something went wrong.',
          icon: 'exclamation',
          className: 'danger',
          time: 5000,
        },
        NotificationType.Error,
      );
    };
    const navigateBack = () => {
      navigate({ path: '/settings/chatbots/mim' });
    };

    const onSubmit: SubmitHandler<FieldValues> = async (data: FieldValues) => {
      const identity = {
        source: data.knowledgeType,
        api_key: data.api_key,
        token: data.token,
        external_source_id: data.external_source_id,
      };

      if (arg?.knowledgeBase) {
        try {
          const updateResponse = await updateKnowledgeBase({
            type: selectedKnowledgeType,
            id: arg.knowledgeBase.id,
            identity,
          }).unwrap();

          if (isResponseWithData(updateResponse)) {
            notifySuccess(
              `"${
                updateResponse.data.name || 'Data source'
              }" has been updated.`,
            );
            hide?.();
            navigateBack();
          }
        } catch (error) {
          notifyError('Error while updating data source');
        }
      } else {
        try {
          const createResponse = await createKnowledgeBase({
            type: selectedKnowledgeType,
            identity: {
              ...identity,
              organization_id: arg.organizationId,
            },
          }).unwrap();
          if (isResponseWithData(createResponse)) {
            notifySuccess(
              `"${
                createResponse.data.name || 'Data source'
              }" has been created.`,
            );
            hide?.();
            navigateBack();
          }
        } catch (error) {
          notifyError('Error while adding data source');
        }
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title={title + ' data source'} onClose={hide}></ModalHead>
        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormGrid>
              {!arg?.knowledgeBase?.source && (
                <FormField>
                  <HookFormSelectionListSingle
                    name="knowledgeType"
                    label="Data source"
                    control={control}
                    componentProps={{
                      getItemTitleByValue: (value) => {
                        const type = knowledgeTypes.find(
                          (t) => t.value === value,
                        );
                        return type!.label;
                      },
                    }}
                  >
                    {knowledgeTypes.map((type) => (
                      <SelectionList.Item key={type.value} value={type.value}>
                        <Text.Paragraph>{type.label}</Text.Paragraph>
                      </SelectionList.Item>
                    ))}
                  </HookFormSelectionListSingle>
                </FormField>
              )}

              {selectedKnowledgeType === 'elevio' && (
                <>
                  <FormField>
                    <HookFormText
                      name="api_key"
                      label="API Key"
                      control={control}
                      noAlert={true}
                      componentProps={{
                        placeholder: 'API Key',
                      }}
                      rules={{
                        required:
                          selectedKnowledgeType === 'elevio' &&
                          'API Key is required',
                      }}
                    />
                  </FormField>
                  <FormField>
                    <HookFormTextarea
                      name="token"
                      label="Token"
                      control={control}
                      noAlert={true}
                      componentProps={{
                        placeholder: 'Token',
                      }}
                      rules={{
                        required:
                          selectedKnowledgeType === 'elevio' &&
                          'Token is required',
                      }}
                    />
                  </FormField>
                </>
              )}

              {selectedKnowledgeType === 'dixa_knowledge' &&
                flags.knowledgeAccess && (
                  <FormField>
                    <HookFormSelectionListSingle
                      name="external_source_id"
                      label="Help centers"
                      control={control}
                      componentProps={{
                        getItemTitleByValue: (value) => {
                          const type = knowledgeData.find(
                            (t) => t.id === value,
                          );
                          return type?.name ? type.name : null;
                        },
                        placeholder: 'Help centers with public access',
                      }}
                    >
                      {knowledgeData.map((item) => (
                        <SelectionList.Item
                          key={item.id}
                          value={item.id}
                          isDisabled={!item.isActive}
                        >
                          <Tag
                            color={
                              item.isActive
                                ? colors.green[400]
                                : colors.red[400]
                            }
                          >
                            {item.name}
                          </Tag>
                        </SelectionList.Item>
                      ))}
                    </HookFormSelectionListSingle>
                  </FormField>
                )}
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          confirmName={arg?.knowledgeBase ? 'Update' : 'Create'}
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isUpdating || isCreating,
            loadingIndicator:
              (isUpdating && 'Updating...') || (isCreating && 'Creating...'),
            form: formId,
            type: 'submit',
            disabled:
              isCreating ||
              isUpdating ||
              !formState.isValid ||
              formState.isSubmitting ||
              !formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
